import { Button, CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

import { useAuth } from '../auth/use-auth';
import TextField from '../auth/TextField';
import SubmitErrorHelper from '../common/SubmitErrorHelper';
import SubmitButtonsContainer from '../common/SubmitButtonsContainer';
import PasswordValidations from '../auth/PasswordValidations';
import AuthFormContainer from '../auth/AuthFormContainer';
import usePasswordRequirements from '../auth/usePasswordRequirements';

export default function ChangePassword({ onCancel }) {
    const { t } = useTranslation();

    const { user } = useAuth();
    const verifyPasswordRequirements = usePasswordRequirements();

    const history = useHistory();
    const { changePassword } = useAuth();

    const validate = values => {
        let errors = {};

        if (!values.currentPassword) {
            errors.currentPassword = t('ChangePassword.CurrentPasswordRequired');
        }

        if (!values.password) {
            errors.password = t('ChangePassword.PasswordRequired');
        } else {
            errors = { ...errors, ...verifyPasswordRequirements(values.password) };
        }

        if (!values.confirmPassword) {
            errors.confirmPassword = t('ChangePassword.ConfirmPasswordRequired');
        } else if (values.confirmPassword !== values.password) {
            errors.confirmPassword = t('ChangePassword.PasswordsDoNotMatch');
        }

        return errors;
    };

    const onSubmit = async (values, { setSubmitting, setErrors }) => {
        try {
            let isPasswordChanged = await changePassword(
                user.username,
                values.password,
                values.currentPassword
            );

            if (isPasswordChanged) {
                enqueueSnackbar(t('ChangePassword.PasswordChanged'), { variant: 'success' });
                history.push('./logout');
                return;
            } else {
                setErrors({ currentPassword: t('ChangePassword.IncorrectPassword') });
            }
        } catch {
            setErrors({ submitError: t('UnknownError') });
        }
        setSubmitting(false);
    };

    return (
        <Formik validate={validate} onSubmit={onSubmit} initialValues={initialValues}>
            {({ values, errors, isSubmitting }) => (
                <AuthFormContainer>
                    <TextField
                        name='currentPassword'
                        label={t('ChangePassword.CurrentPassword')}
                        type='password'
                        autoFocus
                    />
                    <TextField
                        name='password'
                        label={t('ChangePassword.NewPassword')}
                        type='password'
                        autoComplete='new-password'
                    />
                    <TextField
                        name='confirmPassword'
                        label={t('ChangePassword.ConfirmPassword')}
                        type='password'
                        autoComplete='new-password'
                    />

                    <PasswordValidations errors={errors} hasValue={!!values.password} />

                    <SubmitErrorHelper>{errors.submitError}</SubmitErrorHelper>
                    <SubmitButtonsContainer>
                        <Button variant='contained' onClick={onCancel}>
                            {t('Cancel')}
                        </Button>
                        <Button
                            type='submit'
                            variant='contained'
                            color='primary'
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
                        >
                            {t('Continue')}
                        </Button>
                    </SubmitButtonsContainer>
                </AuthFormContainer>
            )}
        </Formik>
    );
}

const initialValues = {
    currentPassword: '',
    password: '',
    confirmPassword: ''
};
